/* eslint-disable string-to-lingui/missing-lingui-transformation */

import {QueryClient, QueryClientProvider} from '@tanstack/react-query'
import {httpBatchLink, httpLink, loggerLink, splitLink, type Operation} from '@trpc/client'
import React from 'react'

import type {RootRouter} from 'bff'
import config from 'config'
import {getJwtExpiryMs} from '~/auth/jwt'
import {getTokenPayload} from '~/auth/token-data'
import {BffErrorMessages, createBffHooks} from '~/bff'
import type {ClientContext} from '~/bff/operational-context'
import {reportError} from '~/error/report'
import {
    getScopedToken,
    useAccessToken,
    __getAccessToken,
    memoForceSessionRefresh,
    useEntitySwitchEffect,
} from '~/shell/session'

const trpcReact = createBffHooks<RootRouter>()

/**
 * A client-side provider for tRPC-based BFF (Backend-For-Frontend). This allows us
 * to use auto-generated SWR hooks to fetch data from our BFF layer. Refer to
 * app/bff for more context.
 */
export const BffProvider: React.FC = ({children}) => {
    const queryClient = React.useRef(
        new QueryClient({
            defaultOptions: {
                queries: {
                    // Requests retry is disabled because it will be implemented within the BFF
                    retry: false,
                    staleTime: 5 * (60 * 1000), // 5 mins
                    cacheTime: 10 * (60 * 1000), // 10 mins
                },
                mutations: {retry: false},
            },
        }),
    )

    const trpcClient = trpcReact.createClient({
        links: [
            loggerLink({enabled: () => config.environment === 'dev'}),
            splitLink({
                condition: shouldEnableBatching,
                true: httpBatchLink({
                    url: removeTrailingSlash(config.endpoints.bff),
                    async headers({opList}) {
                        return getHeaders(opList[0])
                    },
                }),
                false: httpLink({
                    url: removeTrailingSlash(config.endpoints.bff),
                    async headers({op}) {
                        return getHeaders(op)
                    },
                }),
            }),
        ],
    })

    return (
        <trpcReact.Provider client={trpcClient} queryClient={queryClient.current}>
            <EntitySwitchCacheBuster queryClient={queryClient.current} />
            <InvalidateQueries />
            <QueryClientProvider client={queryClient.current}>{children}</QueryClientProvider>
        </trpcReact.Provider>
    )
}

function removeTrailingSlash(str: string) {
    return str.replace(/\/+$/, '')
}

// When the access token changes we need to invalidate all data fetched.
function InvalidateQueries() {
    const accessToken = useAccessToken()
    const utils = trpcReact.useContext()

    React.useEffect(() => {
        utils.invalidate().catch(reportError)
    }, [accessToken, utils])

    return null
}

/**
 * Often BFF queries does not include entity-specific input parameters like company ID
 * or user ID. However, query data usually is entity-specific.
 * So when user switches between accounts/entities BFF query returns cached data for the previous entity
 * since BFF cache key does not include entity-specific information.
 *
 * We run query revalidation on every accessToken change (see InvalidateQueries above)
 * so eventually after a couple of seconds data will be synchronized with the new entity
 * but revalidation takes some time and user will see unrelated data until revalidation is finished.
 *
 * To avoid this we are clearing BFF cache entirely on account/entity switch so developers
 * could implement proper loading indication on account/entity switch.
 *
 * If BFF query is not specific to the entity or it is desired behaviour to keep query data between entity switches
 * just add `keepPreviousData: true` option to the BFF query and it will survive cache clearing.
 * Note that queries with `keepPreviousData: true` still will be revalidated.
 */
function EntitySwitchCacheBuster({queryClient}: {queryClient: QueryClient}) {
    useEntitySwitchEffect(() => {
        queryClient.getQueryCache().clear()
    })

    return null
}

/**
 * Truthy return values will enable query batching.
 * Always skip batching if:
 * - we're mocking requests (https://linear.app/pleo/issue/WEB-256/enable-query-batch-in-e2e-tests)
 * - when using an elevated auth token
 * - if we specifically request a non batched request via `context.skipBatch` flag
 */
function shouldEnableBatching(op: Operation) {
    const {auth, skipBatch: skipBatchFromContext} = op.context as ClientContext

    const useElevatedAuth = !!(auth?.type === 'elevated')
    const isMockingRequests = !!window.Cypress || window.___msw?.active

    const skipBatching = isMockingRequests || useElevatedAuth || !!skipBatchFromContext
    return !skipBatching
}

/**
 * Prepare the operational context for the final request.
 * Based on the operational context and environment, we may need to:
 * - get a new elevated scoped token
 */
async function getAccessToken(op: Operation) {
    const {auth} = op.context as ClientContext

    if (!auth) {
        // No specific auth context specified, we use the current access token.
        // First we make sure it's fresh, if not we refresh it first.
        await refreshTokenIfNeeded()
        return __getAccessToken()
    }

    if (auth.type === 'elevated') {
        // If this is an elevated auth request, we need to get a new scoped token.
        // Note that this will likely show a PIN modal.
        const {elevatedResourceIds, forcePIN, scope} = auth
        const accessToken =
            (await getScopedToken(scope, {
                force: forcePIN,
                elevatedResourceIds,
            })) || null

        if (accessToken === null) {
            throw new Error(BffErrorMessages.PIN_MODAL_CANCELLED)
        }

        return accessToken
    }

    if (auth.type === 'token') {
        // If this is a request with a specific token, we use that token.
        return auth.accessToken
    }

    throw new Error(`Unknown auth type, please use the defineClientContext helper`)
}

async function getHeaders(operation: Operation) {
    const token = await getAccessToken(operation)
    return token ? {Authorization: `Bearer ${token}`} : {}
}

/**
 * Check if the token is expired or about to expire. If so, force refresh the
 * session to acquire a new token.
 */
async function refreshTokenIfNeeded() {
    // 20s is the timeout on our BFF lambda, which means that with 25s here,
    // even in the worst case scenario the token won't expire while the
    // BFF call is in progress, and we should never see 401s coming back.
    const MIN_TOKEN_TTL_MS = 25000

    const accessToken = __getAccessToken()
    if (!accessToken) {
        return
    }
    const tokenPayload = getTokenPayload(accessToken)
    if (!tokenPayload || getJwtExpiryMs(tokenPayload) < MIN_TOKEN_TTL_MS) {
        // We only want to trigger the session refresh once, even if we're dealing with
        // multiple requests triggered at the same time
        await memoForceSessionRefresh()
    }
}
